/* eslint-disable max-lines */
import { getCategoryInfo } from './categories'
import { isEmpty, isNil, isValidOwnerName, isValidPublicProjectName, periodToMinutes } from './helpers'
import { getProjectHref, getUrl } from './uri'
import { ErrorResponse, FetchGenericResponse, get, put } from './network'
import { getGeneralProjects, getProjectsByCategory, getProjectsByTags } from './relatedProjects/requests'
import { MAX_NUMBER_OF_RELATED_PROJECTS } from './relatedProjects/constants'
import { getForumAction } from './forum'
import { GetServerSidePropsContext } from 'next'
import { StaticImageData } from 'next/image'
import { HistoryLog, HistoryLogAction } from '@/interfaces/history'
import { API_BASE_URL, BASE_URL, LONG_URL } from '@/config'
import { ListRouterQuery, TimeFilterType, UserProfilePageQuery } from '@/interfaces/filters'
import { GetProjectsResponse, Project, ProjectsResponse, ProjectsV1Response } from '@/interfaces/projects'
import {
  ACCEPTED_FORUM_ACTIVITY_ACTION_TYPES,
  ForumActivityItem,
  ForumActivityResponse,
  ForumTopic,
  ForumUser
} from '@/interfaces/forum'
import { JobItem, JobsResponse, jobLabelsMap } from '@/interfaces/jobs'
import { Tag } from '@/interfaces/tags'
import { HttpStatus, isServerError } from '@/helpers/HttpStatus'
import { createRequestHeaders, getCookies } from '@/helpers/cookieHelper'
import { Categories, SimpleCategories } from '@/interfaces/categories'
import { Result } from '@/interfaces/results'
import { Attributes, User, UserSettings } from '@/interfaces/users'
import { SendRequestResponse } from '@/interfaces/comments'
import userAvatarPlaceholder from '@/assets/img/user-avatar.png'
import { logData } from '@/logging/nodeLogger'

const INTERNAL_API_URL = `${API_BASE_URL}/api/`
const EXTERNAL_API_URL = `${BASE_URL}/api/`
export const AUTHENTICATION_URL = '/signin/'
export const SIGN_UP_URL = '/signup/'

const timeMap: { [key: string]: string } = {
  week: '7',
  month: '30',
  year: '365'
}

export const getAPIPath = (path: string, version = 'v1', internal = true): string => {
  return `${internal ? INTERNAL_API_URL : EXTERNAL_API_URL}${version}${path}`
}

export const getLongServicePath = (path: string): string => {
  return `${LONG_URL}${path}`
}

export const getLogoPath = (path: String): string => {
  return `${BASE_URL}${path}`
}

export const getResolvedThumbnailFullServicePath = (path: string | null | undefined): string => {
  if (isNil(path)) {
    return null
  }

  if (path.startsWith('http')) {
    const url = new URL(path)
    const search = !isNil(url.search) ? url.search : ''
    path = url.pathname + search
  }
  if (/^\/api\/v1\/projects\/.*\/thumbnail(?:\/.*)?$/.test(path)) {
    return `${LONG_URL}${path}`
  }
  return `${BASE_URL}${path}`
}

export const getForumAPIPath = (path: string, internal = true): string => {
  return `${internal ? API_BASE_URL : BASE_URL}/forum${path}`
}

export const getFilteredProjectsPath = (params?: ListRouterQuery): string => {
  const searchParams = new URLSearchParams()

  // eslint-disable-next-line eqeqeq
  if (params?.tag != null) {
    searchParams.append('tag', String(params?.tag))
  }

  // eslint-disable-next-line eqeqeq
  if (params?.categoryId != null) {
    searchParams.append('category', String(params?.categoryId).toUpperCase())
  }

  searchParams.append('page', String(params?.page ?? 1))
  searchParams.append('limit', params?.limit ?? '48')
  searchParams.append('sort', `-${params?.sortBy ?? 'project_popular'}`)
  searchParams.append('filter', String(params?.search ?? ''))
  const time: TimeFilterType = params?.time ?? 'all'
  if (time !== 'all') {
    searchParams.append('duration', String(timeMap[time]))
  }

  return getAPIPath(`/projects?${searchParams}`, 'v3')
}

export const getProjects = async (
  params?: ListRouterQuery,
  req?: GetServerSidePropsContext['req']
): Promise<GetProjectsResponse> => {
  const projectsUrl = getFilteredProjectsPath(params)
  const projectsResponse = await get<ProjectsResponse>(projectsUrl, {
    headers: createRequestHeaders(req)
  })

  if (isServerError(projectsResponse.status)) {
    return { error: projectsResponse?.data as ErrorResponse }
  }

  if (projectsResponse.status === HttpStatus.OK) {
    const items = projectsResponse?.data?.items.map((project: Project) => {
      const creationDate = new Date(project.creationDate)
      const lastModificationDate = new Date(project.lastModificationDate)
      project.creationDate = new Date(
        creationDate.getTime() - creationDate.getTimezoneOffset() * 60 * 1000
      ).toISOString()
      project.lastModificationDate = new Date(
        lastModificationDate.getTime() - lastModificationDate.getTimezoneOffset() * 60 * 1000
      ).toISOString()
      project.thumbnailUrl = getResolvedThumbnailFullServicePath(project.thumbnailUrl)
      if (!isNil(project.parentProject)) {
        project.parentProject.thumbnailUrl = getResolvedThumbnailFullServicePath(project.parentProject.thumbnailUrl)
      }
      return project
    })

    return {
      items,
      pagingMetadata: projectsResponse?.data?.pagingMetadata
    }
  }

  return {
    items: [],
    pagingMetadata: null
  }
}

export const getFilteredUserProjectsPath = (params?: UserProfilePageQuery): string => {
  const searchParams = new URLSearchParams()

  const limit = params?.limit ?? '48'
  const offset = params?.page ? String((+params?.page - 1) * +limit) : '0'
  searchParams.append('limit', limit)
  searchParams.append('offset', offset)
  searchParams.append('sortby', String(params?.sortBy ?? 'project_popular'))
  searchParams.append('order', 'desc')

  searchParams.append('filter', String(params?.search ?? ''))
  searchParams.append('filterby', 'project_text')
  searchParams.append('scope', 'PUBLIC_PROJECTS')

  return getAPIPath(`/projects/${params.username}?${searchParams}`, 'v1')
}

export const getUserProjects = async (
  params?: UserProfilePageQuery,
  req?: GetServerSidePropsContext['req']
): Promise<GetProjectsResponse> => {
  const projectsResponse = await get<ProjectsV1Response>(getFilteredUserProjectsPath(params), {
    headers: createRequestHeaders(req)
  })

  if (isServerError(projectsResponse.status)) {
    return { error: projectsResponse?.data as ErrorResponse }
  }

  if (projectsResponse.status === HttpStatus.OK) {
    const items = projectsResponse?.data?.projects.map((project: Project) => {
      const creationDate = new Date(project.creationDate)
      const lastModificationDate = new Date(project.lastModificationDate)
      project.creationDate = new Date(
        creationDate.getTime() - creationDate.getTimezoneOffset() * 60 * 1000
      ).toISOString()
      project.lastModificationDate = new Date(
        lastModificationDate.getTime() - lastModificationDate.getTimezoneOffset() * 60 * 1000
      ).toISOString()
      project.thumbnailUrl = getResolvedThumbnailFullServicePath(project.thumbnailUrl)
      if (!isNil(project.parentProject)) {
        project.parentProject.thumbnailUrl = getResolvedThumbnailFullServicePath(project.parentProject.thumbnailUrl)
      }
      return project
    })

    const nextPage = params?.page ? String(+params?.page + 1) : '2'
    const nextPageProjectsResponse = await get<ProjectsV1Response>(
      getFilteredUserProjectsPath({
        ...params,
        page: nextPage
      }),
      {
        headers: createRequestHeaders(req)
      }
    )
    const hasNextPage =
      nextPageProjectsResponse?.status === HttpStatus.OK && !isEmpty(nextPageProjectsResponse?.data?.projects)

    return {
      items,
      pagingMetadata: {
        nextPage: hasNextPage ? nextPage : null,
        pageSize: Number(params?.limit ?? 48),
        page: Number(params?.page ?? 1)
      }
    }
  }

  return {
    items: [],
    pagingMetadata: null
  }
}

const filterTags = (tags: Tag[]): Tag[] => {
  return tags.filter((tag) => {
    return /^[a-zA-Z0-9 ]+$/.test(tag.name) && tag.count >= 3
  })
}

export const getTags = async (
  prefix: string,
  limit: number,
  req?: GetServerSidePropsContext['req']
): Promise<Tag[]> => {
  let tagsRequestUrl = getAPIPath(`/projects/tags?limit=${limit}`)
  if (!isNil(prefix) && prefix.length > 0) {
    tagsRequestUrl += `&prefix=${prefix}`
  }
  const tagsResponse = await get<Tag[]>(tagsRequestUrl, {
    headers: createRequestHeaders(req)
  })
  if (tagsResponse.status !== HttpStatus.OK) {
    return []
  }
  return filterTags(tagsResponse.data)
}

export const getPopularTags = async (req?: GetServerSidePropsContext['req']): Promise<Tag[]> => {
  const tagsResponse = await get<Tag[]>(getAPIPath('/projects/popular_tags'), {
    headers: createRequestHeaders(req)
  })
  if (tagsResponse.status !== HttpStatus.OK) {
    return []
  }
  return filterTags(tagsResponse.data)
}

export const getCategories = async (req?: GetServerSidePropsContext['req']): Promise<SimpleCategories> => {
  const categoriesResponse = await get<SimpleCategories>(getAPIPath('/projects/categories/all'), {
    headers: createRequestHeaders(req)
  })
  if (categoriesResponse.status !== 200) {
    return {
      industries: [],
      analysisTypes: [],
      cadTypes: []
    }
  }
  return categoriesResponse.data
}

export const checkIfCategoryExists = async (
  categoryName: string,
  req?: GetServerSidePropsContext['req']
): Promise<boolean> => {
  categoryName = categoryName.toUpperCase()
  const categories = await getCategories(req)
  return (
    categories.industries.find((category) => category.name === categoryName) !== undefined ||
    categories.analysisTypes.find((category) => category.name === categoryName) !== undefined ||
    categories.cadTypes.find((category) => category.name === categoryName) !== undefined
  )
}

export const getGroupedCategories = async (req?: GetServerSidePropsContext['req']): Promise<Categories> => {
  const categories = await getCategories(req)
  const groupedCategories: Categories = {
    industries: categories.industries
      .map((category) => getCategoryInfo(category.name))
      .filter((category) => !isEmpty(category))
      .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName)),
    analysisTypes: categories.analysisTypes
      .map((category) => getCategoryInfo(category.name))
      .filter((category) => !isEmpty(category))
      .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName)),
    cadTypes: categories.cadTypes
      .map((category) => getCategoryInfo(category.name))
      .filter((category) => !isEmpty(category))
      .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))
  }
  return groupedCategories
}

export const getProject = async (
  ownerName: string,
  publicProjectName: string,
  options?: any,
  internal: boolean = true
): Promise<Project | null> => {
  if (!isValidOwnerName(ownerName) || !isValidPublicProjectName(publicProjectName)) {
    return null
  }

  const projectResponse = await get<Project>(
    getAPIPath(`/projects/${ownerName}/${publicProjectName}`, 'v1', internal),
    options
  )
  if (projectResponse.status !== HttpStatus.OK) {
    return null
  }

  const creationDate = new Date(projectResponse.data.creationDate)
  const lastModificationDate = new Date(projectResponse.data.lastModificationDate)
  projectResponse.data.creationDate = new Date(
    creationDate.getTime() - creationDate.getTimezoneOffset() * 60 * 1000
  ).toISOString()
  projectResponse.data.lastModificationDate = new Date(
    lastModificationDate.getTime() - lastModificationDate.getTimezoneOffset() * 60 * 1000
  ).toISOString()
  projectResponse.data.thumbnailUrl = getResolvedThumbnailFullServicePath(projectResponse.data.thumbnailUrl)
  if (!isNil(projectResponse.data.parentProject)) {
    projectResponse.data.parentProject.thumbnailUrl = getResolvedThumbnailFullServicePath(
      projectResponse.data.parentProject.thumbnailUrl
    )
  }

  return projectResponse.data
}

export const getForumSession = async (internal: boolean = true): Promise<string | null> => {
  const forumSessionResponse = await get<{ csrf?: string }>(getForumAPIPath('/session/csrf.json', internal))
  if (forumSessionResponse.status !== HttpStatus.OK) {
    return null
  }
  return forumSessionResponse.data?.csrf
}

export const getForumUser = async (username: string, internal: boolean = true): Promise<ForumUser | null> => {
  if (isNil(username)) {
    return null
  }
  const forumSession = await getForumSession(internal)
  const forumUserResponse = await get<ForumUser>(getForumAPIPath(`/users/${username}.json`, internal), {
    headers: {
      'X-CSRF-Token': forumSession
    }
  })
  if (forumUserResponse.status !== HttpStatus.OK) {
    if (forumUserResponse.status === HttpStatus.NotFound) {
      // eslint-disable-next-line no-console
      console.warn(`Error getting forum user: ${username} doesn't have a forum account`)
    }
    return null
  }
  return forumUserResponse.data
}

export const getForumActivity = async (username: string): Promise<ForumActivityItem[] | null> => {
  if (isNil(username)) {
    return null
  }
  const forumSession = await getForumSession(false)
  const forumActivityResponse = await get<ForumActivityResponse>(
    getForumAPIPath(`/user_actions.json?username=${username}&limit=100`, false),
    {
      headers: {
        'X-CSRF-Token': forumSession
      }
    }
  )
  if (forumActivityResponse.status !== HttpStatus.OK) {
    if (forumActivityResponse.status === HttpStatus.NotFound) {
      // eslint-disable-next-line no-console
      console.warn(`Error getting forum user: ${username} doesn't have a forum activity history`)
    }
    return null
  }
  const forumActivityItems = forumActivityResponse.data.user_actions
  return forumActivityItems
    .filter((item) => ACCEPTED_FORUM_ACTIVITY_ACTION_TYPES.includes(item.action_type))
    .map((item) => {
      item.userAvatarUrl = getUrl(item.acting_avatar_template?.replace('{size}', '66'))
      item.action = getForumAction(item.action_type)
      item.actionUrl = getForumAPIPath(`/t/${item.slug}/${item.topic_id}`, false)
      return item
    })
}

export const getUserData = async (req?: GetServerSidePropsContext['req']): Promise<User | null> => {
  const userDataResponse = await get<User>(getAPIPath(`/users/user`, 'v1', false), {
    headers: createRequestHeaders(req)
  })
  if (userDataResponse.status !== HttpStatus.OK) {
    return null
  }
  const user = userDataResponse.data
  user.fullname = `${user.firstname} ${user.lastname}`.trim()
  return user
}

export const getAttributes = async (
  userId?: number,
  organizationId?: string,
  req?: GetServerSidePropsContext['req']
): Promise<Attributes | null> => {
  const queryParams = new URLSearchParams()
  if (!isNil(userId)) {
    queryParams.append('user', userId.toString())
  }
  if (!isNil(organizationId)) {
    queryParams.append('organizationId', organizationId)
  }
  const response = await get<Attributes>(getAPIPath(`/organizations/attributes?${queryParams}`, 'v2', false), {
    headers: createRequestHeaders(req)
  })
  if (response.status !== HttpStatus.OK) {
    return null
  }
  return response.data
}

export const getUserSettings = async (
  username: string,
  req?: GetServerSidePropsContext['req']
): Promise<UserSettings | null> => {
  const userSettingsResponse = await get<UserSettings>(
    getAPIPath(`/users/${encodeURIComponent(username)}/settingsmap`, 'v1', false),
    {
      headers: createRequestHeaders(req)
    }
  )
  if (userSettingsResponse.status !== HttpStatus.OK) {
    return null
  }
  return userSettingsResponse.data
}

export const getOwnerAvatarUrl = async (username: string, internal: boolean = true): Promise<string> => {
  const forumUser = await getForumUser(username, internal)
  return forumUser?.user ? getUrl(forumUser?.user?.avatar_template?.replace('{size}', '200')) : ''
}

export const getProjectHistory = async (
  project: Project,
  req?: GetServerSidePropsContext['req'],
  internal: boolean = true
): Promise<HistoryLog[]> => {
  const history: HistoryLog[] = []
  if (project.parentProject?.ownerName != null && project.parentProject?.publicProjectName != null) {
    const parentProject = await getProject(
      project.parentProject?.ownerName,
      project.parentProject?.publicProjectName,
      {
        headers: createRequestHeaders(req)
      },
      internal
    )
    if (parentProject && parentProject.requesterPermissions.canReadProject) {
      let parentProjectOwnerAvatarUrl = ''
      if (parentProject.ownerName === project.ownerName) {
        parentProjectOwnerAvatarUrl = project.ownerAvatarUrl
      } else {
        parentProjectOwnerAvatarUrl = await getOwnerAvatarUrl(parentProject.ownerName, internal)
      }

      history.push({
        ownerName: parentProject.ownerName,
        ownerAvatarUrl: parentProjectOwnerAvatarUrl,
        action: HistoryLogAction.CREATED,
        date: parentProject.creationDate,
        publicProjectName: parentProject.publicProjectName,
        projectUrl: getProjectHref(parentProject.ownerName, parentProject.publicProjectName)
      })
    }
  }
  history.push({
    ownerName: project.ownerName,
    ownerAvatarUrl: project.ownerAvatarUrl,
    action: !isEmpty(history) ? HistoryLogAction.COPIED : HistoryLogAction.CREATED,
    date: project.creationDate,
    publicProjectName: project.publicProjectName
  })

  return history
}

export const getProjectForumTopicId = async (ownerName: string, publicProjectName: string): Promise<string | null> => {
  const topicIdResponse = await get<{ id: string }>(
    getAPIPath(`/projects/${ownerName}/${publicProjectName}/topicid`, 'v1', false)
  )
  if (topicIdResponse.status !== HttpStatus.OK) {
    return null
  }
  return topicIdResponse.data?.id
}

export const getProjectForumTopic = async (topicId: string): Promise<FetchGenericResponse<ForumTopic | null>> => {
  if (topicId != null) {
    const topicResponse = await get<ForumTopic>(getForumAPIPath(`/t/${topicId}.json?print=true`, false))
    return topicResponse
  }
  return null
}

export const getProjectJobs = async (
  projectIdExt: string,
  limit: number,
  req?: GetServerSidePropsContext['req']
): Promise<JobItem[]> => {
  let jobs: JobItem[] = []

  const jobsResponse = await get<JobsResponse>(
    getAPIPath(`/projects/${projectIdExt}/aggregated/jobsandresults?limit=${limit}&order=desc`, 'v2', false),
    {
      headers: createRequestHeaders(req)
    }
  )
  if (jobsResponse.status === HttpStatus.OK) {
    if (!isEmpty(jobsResponse.data?.jobs) && !isEmpty(jobsResponse.data?.results)) {
      jobs = jobsResponse.data.jobs
        .map((job) => {
          const result = jobsResponse.data?.results.find((r) => r.uuid === job.resultUuid)
          if (!result) {
            return null
          }

          const duration = periodToMinutes(job.duration)
          const computingTime = periodToMinutes(job.computingTime)

          let scaleFactor = 1
          let showGPUh = false
          if (job.toolType === 'PACEFISH' || job.toolType === 'WIND_COMFORT') {
            scaleFactor = 50
            showGPUh = true
          }

          const durationMinutes = duration.toFixed(0)
          const coreHours = `${(computingTime / 60 / scaleFactor).toFixed(1)} ${showGPUh ? 'GPUh' : 'CPUh'}`
          return {
            key: job.jobUuid,
            progress: job.progress * 100,
            status: job.runningStatus,
            jobTypeLabel: jobLabelsMap[job.jobType] ?? job.jobType,
            durationMinutes,
            coreHours
          }
        })
        .filter((job) => job != null)
    }
    return jobs
  }
}

export const getProjectResults = async (
  project: Project,
  req?: GetServerSidePropsContext['req']
): Promise<Result[]> => {
  const resultsResponse = await get<Result[]>(
    getAPIPath(`/projects/${project.ownerName}/${project.publicProjectName}/results`, 'v1', false),
    {
      headers: createRequestHeaders(req)
    }
  )

  if (resultsResponse.status !== HttpStatus.OK) {
    throw new Error(resultsResponse.statusText)
  }

  if (!isEmpty(resultsResponse.data)) {
    const rawList = resultsResponse.data
    const reversedList = rawList
      ?.slice()
      .reverse()
      .map((result: Result) => ({
        ...result,
        projectIdExt: project.projectIdExt
      }))
    return reversedList
  }
  return []
}

export const getAvatarUrl = (userData: ForumUser, size: string): string | StaticImageData => {
  const template = userData?.user?.avatar_template
  if (template == null) {
    return userAvatarPlaceholder
  }
  return `${BASE_URL}${template?.replace('{size}', size)}`
}

/**
 * https://simscale.atlassian.net/browse/ENG-40475
 * For a given project, display the first 5 results for the list of projects in the same åcategory, sorted by “number of copies”
 * If the project doesn’t have a category, do the same for the project’s first tag, i.e. display the first 5 results for the list of projects that share the first tag, sorted by “number of copies”.
 * If the first tag does not have a sufficient number of projects, then fill in the remaining spots with the top projects from the second tag, sorted by “number of copies”.
 * If all the project’s tags do not have a sufficient number of projects combined, then fill in the remaining spots with the first results from the general list of projects, sorted by “number of copies”.
 * If the project doesn’t have a category nor a tag, display the first 5 results from the general list of projects, sorted by “number of copies”.
 */
export const getProjectRelatedProjects = async (
  project: Project,
  req: GetServerSidePropsContext['req']
): Promise<Project[]> => {
  let results: Project[] = []

  if (!isEmpty(project.categories)) {
    results = await getProjectsByCategory(project.projectIdExt, project.categories[0], req)
  }

  if (!isEmpty(project.tags)) {
    const projectsByTag = await getProjectsByTags(project.projectIdExt, project.tags, 5 - results.length, req)
    results = [...results, ...projectsByTag]
  }

  if (results.length < MAX_NUMBER_OF_RELATED_PROJECTS) {
    const generalProjects = await getGeneralProjects(project.projectIdExt, req)
    results = [...results, ...generalProjects]
  }

  results = results.map((project) => {
    project.thumbnailUrl = getResolvedThumbnailFullServicePath(project.thumbnailUrl)
    if (!isNil(project.parentProject)) {
      project.parentProject.thumbnailUrl = getResolvedThumbnailFullServicePath(project.parentProject.thumbnailUrl)
    }
    return project
  })

  return results.slice(0, MAX_NUMBER_OF_RELATED_PROJECTS)
}

export const sendCommentRequest = async (
  commentBody: string,
  topic_id: string
): Promise<FetchGenericResponse<SendRequestResponse>> => {
  const forumSession = await getForumSession(false)
  const commentResponse = await fetch(getUrl(`/forum/posts`), {
    method: 'POST',
    body: JSON.stringify({ raw: commentBody, topic_id }),
    headers: {
      'X-CSRF-Token': forumSession,
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json, text/javascript, */*; q=0.01'
    }
  }).catch((error) => error)
  return commentResponse
}

export const createTopicRequest = async (project: Project): Promise<string | null> => {
  const createTopicUrl = getAPIPath(`/projects/${project.ownerName}/${project.publicProjectName}/topic`, 'v1', false)

  const topicResponse = await fetch(createTopicUrl, {
    method: 'PUT',
    headers: {
      ...createRequestHeaders(),
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json, text/javascript, */*; q=0.01'
    }
  })

  if (topicResponse.status === HttpStatus.OK || topicResponse.status === HttpStatus.LongRunning) {
    const topicIdResponse = await getProjectForumTopicId(project.ownerName, project.publicProjectName)
    return topicIdResponse
  }
  return null
}

export const getUser = async (
  username: string,
  req?: GetServerSidePropsContext['req'],
  internal: boolean = true
): Promise<User> => {
  const userResponse = await get<User>(getAPIPath(`/users/${username}`, 'v1', internal), {
    headers: createRequestHeaders(req)
  })
  if (userResponse.status === HttpStatus.OK) {
    const user = userResponse.data
    user.fullname = `${user.firstname} ${user.lastname}`.trim()
    return user
  }
  return null
}

export const getLoggedUserData = async (req?: GetServerSidePropsContext['req']): Promise<User> => {
  const cookies = req?.cookies || getCookies()
  if (!cookies?.sid && !cookies?.uid) {
    return null
  }
  const userData = await getUserData(req)
  if (!userData) {
    return null
  }

  const forumUser = await getForumUser(userData?.username, false)
  if (forumUser) {
    userData.avatarUrl =
      typeof forumUser === 'object' && forumUser !== null ? getAvatarUrl(forumUser, '66') : userAvatarPlaceholder
  }
  return userData
}

export const logProjectView = async (
  ownerName: string,
  publicProjectName: string,
  req?: GetServerSidePropsContext['req']
): Promise<void> => {
  const url = getAPIPath(`/projects/${ownerName}/${publicProjectName}/views`, 'v1', false)
  try {
    await put(
      url,
      {},
      {
        headers: createRequestHeaders(req)
      }
    )
  } catch (error) {
    // eslint-disable-next-line no-console
    if (error instanceof Error) {
      logData({
        msg: error.message,
        severity: 'error',
        clientUrl: req?.url,
        stackTrace: error.stack,
        userAgent: req?.headers['user-agent'],
        userId: req?.cookies['uid'],
        service: 'fe-public-projects-ssr'
      })
    }
  }
}

export const getOrganization = async (organizationId: number): Promise<{ name: string } | null> => {
  const organizationResponse = await get<{ name: string }>(
    getAPIPath(`/organizations/${organizationId}`, 'v2', false),
    {
      headers: createRequestHeaders()
    }
  )
  if (organizationResponse.status !== HttpStatus.OK) {
    return null
  }
  return organizationResponse.data
}
